import React, { useState } from 'react';
import { uploadDocument } from '../../services/adminApi';
import { notify } from '../../utils/notify';
import './DocumentUploader.css';

function DocumentUploader({ collectionName, collections, onUploadSuccess, onCancel }) {
  const [uploadData, setUploadData] = useState({
    file: null,
    title: '',
    description: '',
    tags: '',
    chunkSize: 1536,
    previewText: null
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFileForPreview(file);
    }
  };

  const processFileForPreview = async (file) => {
    setFilePreviewLoading(true);
    try {
      let previewText = '';
      
      if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
        const text = await file.text();
        previewText = text.substring(0, 2000); // Preview first 2000 chars
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        previewText = '[PDF Preview] - PDF files will be processed on upload. Content preview not available for PDF files.';
      } else if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
        previewText = '[DOCX Preview] - DOCX files will be processed on upload. Content preview not available for DOCX files.';
      } else {
        previewText = 'Preview not available for this file type.';
      }
      
      setUploadData({ 
        ...uploadData, 
        file, 
        title: uploadData.title || file.name.replace(/\.[^/.]+$/, ""),
        previewText 
      });
    } catch (err) {
      console.error('Error processing file for preview:', err);
      setUploadData({ 
        ...uploadData, 
        file, 
        title: uploadData.title || file.name.replace(/\.[^/.]+$/, ""),
        previewText: 'Error loading preview' 
      });
    } finally {
      setFilePreviewLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      const validExtensions = ['.pdf', '.docx', '.txt', '.md'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (validExtensions.includes(fileExtension)) {
        processFileForPreview(file);
      } else if (fileExtension === '.doc') {
        notify.warning('Legacy .doc format is not supported. Please convert your document to .docx format or save as PDF/TXT.');
      } else {
        notify.warning('Invalid file type. Please upload PDF, DOCX, TXT, or MD files.');
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !collectionName) {
      notify.warning('Please select a file and collection');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      await uploadDocument(
        collectionName,
        uploadData.file,
        uploadData.title || null,
        uploadData.description || null,
        uploadData.tags || null,
        uploadData.chunkSize || 1536,
        (progress) => {
          setUploadProgress(progress);
        }
      );
      notify.success('Document uploaded successfully!');
      setUploadData({ file: null, title: '', description: '', tags: '', chunkSize: 1536, previewText: null });
      setUploadProgress(0);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      notify.error('Failed to upload document: ' + (err.response?.data?.detail || err.message));
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleClose = () => {
    setUploadData({ file: null, title: '', description: '', tags: '', chunkSize: 1536, previewText: null });
    setUploadProgress(0);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="document-uploader-modal" role="dialog" aria-labelledby="upload-document-title" aria-modal="true">
      <div className="document-uploader-content">
        <div className="document-uploader-header">
          <h3 id="upload-document-title">Upload Document</h3>
          <button
            className="close-button"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClose();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleClose();
              }
            }}
            aria-label="Close upload window"
            type="button"
          >
            ×
          </button>
        </div>
        <div className="upload-form" role="form" aria-label="Document upload form">
          <div className="form-group">
            <label htmlFor="upload-collection">Collection</label>
            <select
              id="upload-collection"
              value={collectionName}
              onChange={(e) => {
                if (onUploadSuccess) {
                  onUploadSuccess({ collectionChanged: e.target.value });
                }
              }}
              disabled
              aria-label="Target collection for document upload"
            >
              {collections && collections.map((col) => (
                <option key={col.name} value={col.name}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="upload-file">File (PDF, DOCX, TXT, MD)</label>
            <small style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
              Note: Only .docx format is supported (not legacy .doc files)
            </small>
            <div 
              className={`file-upload-area ${isDragging ? 'dragging' : ''} ${uploadData.file ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              aria-label="File upload area. Drag and drop a file here or click to browse."
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  document.getElementById('hidden-file-input')?.click();
                }
              }}
            >
              {uploadData.file ? (
                <div className="file-selected">
                  <span className="file-name" aria-label={`Selected file: ${uploadData.file.name}`}>{uploadData.file.name}</span>
                  <span className="file-size" aria-label={`File size: ${formatFileSize(uploadData.file.size)}`}>({formatFileSize(uploadData.file.size)})</span>
                  <button 
                    type="button" 
                    className="remove-file"
                    onClick={() => setUploadData({ ...uploadData, file: null, previewText: null })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setUploadData({ ...uploadData, file: null, previewText: null });
                      }
                    }}
                    aria-label="Remove selected file"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <div className="upload-icon" aria-hidden="true">📄</div>
                  <p>Drag & drop a file here, or <label className="browse-link" htmlFor="hidden-file-input">browse<input id="hidden-file-input" type="file" onChange={handleFileSelect} accept=".pdf,.docx,.txt,.md" style={{ display: 'none' }} aria-label="Browse for file to upload" /></label></p>
                  <p className="upload-hint">Supported: PDF, DOCX (not .doc), TXT, MD</p>
                </>
              )}
            </div>
            {filePreviewLoading && (
              <div className="preview-loading">Processing file...</div>
            )}
            {uploadData.previewText && (
              <div className="file-preview">
                <strong>Preview:</strong>
                <pre>{uploadData.previewText}</pre>
                {uploadData.previewText.length >= 2000 && (
                  <p className="preview-note">(Showing first 2000 characters)</p>
                )}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="upload-chunk-size">Chunk Size (tokens, default: 1536)</label>
            <input
              id="upload-chunk-size"
              type="number"
              min="100"
              max="2000"
              value={uploadData.chunkSize}
              onChange={(e) => setUploadData({ ...uploadData, chunkSize: parseInt(e.target.value) || 1536 })}
              placeholder="1536"
              aria-label="Chunk size in tokens"
              aria-describedby="chunk-size-hint"
            />
            <small id="chunk-size-hint">Number of tokens per chunk. Recommended: 512-2000 (default matches embedding dimensions)</small>
          </div>
          <div className="form-group">
            <label htmlFor="upload-title">Title (optional)</label>
            <input
              id="upload-title"
              type="text"
              value={uploadData.title}
              onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
              placeholder="Document title"
              aria-label="Document title"
            />
          </div>
          <div className="form-group">
            <label htmlFor="upload-description">Description (optional)</label>
            <textarea
              id="upload-description"
              value={uploadData.description}
              onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
              placeholder="Document description"
              rows="3"
              aria-label="Document description"
            />
          </div>
          <div className="form-group">
            <label htmlFor="upload-tags">Tags (comma-separated, optional)</label>
            <input
              id="upload-tags"
              type="text"
              value={uploadData.tags}
              onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
              placeholder="tag1, tag2, tag3"
              aria-label="Document tags"
            />
          </div>
          {uploading && (
            <div className="upload-progress-container">
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="upload-progress-text">{uploadProgress}%</div>
            </div>
          )}
          <div className="upload-actions" role="group" aria-label="Upload actions">
            <button
              className="upload-submit"
              onClick={handleUpload}
              disabled={uploading || !uploadData.file}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !uploading && uploadData.file) {
                  e.preventDefault();
                  handleUpload();
                }
              }}
              aria-label="Upload document"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <button
              className="upload-cancel"
              onClick={() => {
                setUploadData({ file: null, title: '', description: '', tags: '', chunkSize: 1536, previewText: null });
                setUploadProgress(0);
                if (onCancel) {
                  onCancel();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setUploadData({ file: null, title: '', description: '', tags: '', chunkSize: 1536, previewText: null });
                  setUploadProgress(0);
                  if (onCancel) {
                    onCancel();
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setUploadProgress(0);
                  if (onCancel) {
                    onCancel();
                  }
                }
              }}
              aria-label="Cancel document upload"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentUploader;

